import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

export interface ReportParams {
  start_date?: string; end_date?: string; city_id?: number
  vendor_id?: number; trainer_id?: number; page?: number; limit?: number
}

export type ReportType = 'booking' | 'revenue' | 'trainer' | 'student' | 'school'

// ── Confirmed response row shapes (verified against live API responses) ─────
export interface BookingReportRow {
  'School ID': number
  'School Name': string
  'Total Number of Bookings': number
  'Total Completed Bookings': number
  'Total Paid Bookings': number
  'Total Sessions': number
  'Completed Sessions': number
  'Cancelled Sessions': number
  'Pending Sessions': number
  'Rescheduled Sessions': number
  'Active Trainers': number
  'Total Students': number
}

export interface RevenueReportTrainerRow {
  'Trainer Name': string
  'Revenue': number
  'Total Number Of Bookings': number
  'Total Number Of Completed Bookings': number
}

export interface RevenueReportRow {
  'School Name': string
  'Bookings Revenue By School': number
  'Bookings Revenue By Trainer': RevenueReportTrainerRow[] | null
  'Total Number Of Bookings': number
  'Total Number Of Completed Bookings': number
}

export interface TrainerReportRow {
  'Trainer ID': number
  'Trainer Name': string
  'School Name': string
  'Total Bookings': number
  'Total Completed Bookings': number
  'Total Sessions': number
  'Cancellation Rate': string
  // Admin-wide report returns a number (0 when unrated); the school-scoped report returns the string "N/A" instead.
  'Average Rating': number | string
}

export interface StudentReportRow {
  'Student ID': number
  'Student Name': string
  'Category': string
  'Total Sessions': number
  'Completed Sessions': number
  'Amount Paid': number
  'Certificate Issued': string
}

export interface SchoolReportRow {
  'School ID': number
  'School Name': string
  'School Admin Email': string
  'School Admin Phone Number': string
  'Trainers Count': number
}

export interface ReportResponse<T> {
  status: string
  message: string
  total: number
  count_per_page: number
  data: T[]
  // Only present on the revenue report
  total_bookings_revenue?: number
}

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
).toString()

const build = (p: ReportParams) => {
  const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 15 }
  if (p.start_date) params.start_date = p.start_date
  if (p.end_date)   params.end_date   = p.end_date
  if (p.city_id)    params.city_id    = p.city_id
  if (p.vendor_id)  params.vendor_id  = p.vendor_id
  if (p.trainer_id) params.trainer_id = p.trainer_id
  return q(params)
}

export const reportService = {
  // Preview (paginated table data) — admin-wide
  bookingsPreview: (p: ReportParams = {}) =>
    restClient.get(`${endpoints.reportPreview.booking}?${build(p)}`).then(r => r.data as ReportResponse<BookingReportRow>),
  revenuePreview: (p: ReportParams = {}) =>
    restClient.get(`${endpoints.reportPreview.revenue}?${build(p)}`).then(r => r.data as ReportResponse<RevenueReportRow>),
  trainerPreview: (p: ReportParams = {}) =>
    restClient.get(`${endpoints.reportPreview.trainer}?${build(p)}`).then(r => r.data as ReportResponse<TrainerReportRow>),
  studentPreview: (p: ReportParams = {}) =>
    restClient.get(`${endpoints.reportPreview.student}?${build(p)}`).then(r => r.data as ReportResponse<StudentReportRow>),
  schoolPreview: (p: ReportParams = {}) =>
    restClient.get(`${endpoints.reportPreview.school}?${build(p)}`).then(r => r.data as ReportResponse<SchoolReportRow>),
  // Download (CSV blob) — admin only, no school-scoped equivalent exists on the backend
  download: (type: ReportType, p: ReportParams = {}) =>
    restClient.get(`${(endpoints.reportDownload as Record<string, string>)[type]}?${build(p)}`, { responseType: 'blob' }).then(r => r.data),

  /**
   * Same request, but keeps the response headers so the caller can use the
   * server's filename and detect an error payload that came back as a blob
   * (a failed download still resolves with responseType: 'blob').
   */
  downloadFile: async (type: ReportType, p: ReportParams = {}) => {
    const url = (endpoints.reportDownload as Record<string, string>)[type]
    if (!url) throw new Error(`No download endpoint for report type "${type}"`)
    const res = await restClient.get(`${url}?${build(p)}`, { responseType: 'blob' })
    const blob: Blob = res.data

    // A JSON/HTML body here means the API refused the export — read it and throw
    // the real message instead of saving a broken file.
    const ctype = String(res.headers?.['content-type'] ?? blob.type ?? '')
    if (/application\/json|text\/html/i.test(ctype)) {
      const text = await blob.text()
      let msg = text
      try { msg = JSON.parse(text)?.message ?? text } catch { /* keep raw text */ }
      throw new Error(msg || 'Export failed')
    }

    // Prefer the server's filename; fall back to <type>-report.<ext by mime>.
    const disp = String(res.headers?.['content-disposition'] ?? '')
    const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)?.[1]
    const ext = /sheet|excel|xlsx/i.test(ctype) ? 'xlsx' : /pdf/i.test(ctype) ? 'pdf' : 'csv'
    const filename = named ? decodeURIComponent(named) : `${type}-report.${ext}`

    return { blob, filename }
  },
  // School admin — same field names, scoped server-side to the logged-in school admin's vendor.
  // Since a school admin only has one school, any report scoped down to "one school's own
  // row" (revenue, school) comes back as a single bare object (no `data[]`/`total`/
  // `count_per_page`) instead of the paginated list shape every other report type uses —
  // normalize it here regardless of type, so the table always gets an array to render.
  schoolReportPreview: async <T>(type: ReportType, p: ReportParams = {}) => {
    const raw = await restClient.get(`${(endpoints.schoolReportPreview as Record<string, string>)[type]}?${build(p)}`).then(r => r.data)
    if (raw?.data && !Array.isArray(raw.data)) {
      return { ...raw, data: [raw.data], total: raw.total ?? 1, count_per_page: raw.count_per_page ?? 1 } as ReportResponse<T>
    }
    return raw as ReportResponse<T>
  },
}
